import React, { FC, memo, useCallback, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';

import { gatewayService } from 'services';
import { Spinner } from 'components/atoms';
import { AdminPageProps } from 'components/AppRouter';
import { Client } from 'types/Client';
import { Customer } from 'types/Customer';
import { NetworkError } from 'types/Error';
import { ITableHeader, CellType } from 'types/Header';
import { theme } from 'theme';
import {
  Card,
  AdminFormLine,
} from 'theme/mixins';
import {
  getNetworkErrors,
  formatCurrency
} from 'utils/general';
import {
  useDebouncedCallback,
} from 'components/hooks';

import { Search } from './';
import { useSortFilter } from '../../hooks';
import { PageHeader, Table } from '../../components';

import {
  Wrapper,
} from './Customers.styles';

interface State {
  client: {
    loading: boolean;
    data: Client | null;
    error: NetworkError | null;
  };
  customers: {
    loading: boolean;
    data: Customer[] | null;
    total: number | null;
    fields: any;
    offset: number;
    error: NetworkError | null;
  };
}

const initialState: State = {
  client: {
    loading: false,
    data: null,
    error: null
  },
  customers: {
    loading: false,
    total: null,
    data: null,
    fields: {},
    offset: 0,
    error: null
  }
};

const Customers: FC<AdminPageProps> = props => {
  const {
    addToast,
  } = props;

  const navigate = useNavigate();
  const [state, setState] = useState<State>(initialState);
  const { clientId } = useParams();

  const {
    renderPaginationAndSort: customerPaginationMarkup,
    sortState: customerSortState,
    setSortState: customerSetSortState,
  } = useSortFilter({
    listItemId: 'Customer',
    total: state.customers.total,
    offset: state.customers.offset,
    onPrev: (opts) => fetchCustomers(opts),
    onNext: (opts) => fetchCustomers(opts),
    onReset: (opts) => fetchCustomers(opts),
    onFilter: (opts) => fetchCustomers(opts),
    onSort: (opts) => fetchCustomers(opts)
  });

  const fetchCustomers = useCallback((opts?: any, query?: string) => {
    if (state.customers.loading) {
      return;
    }

    setState(prevState => ({
      ...prevState,
      customers: {
        ...prevState.customers,
        loading: true
      }
    }));

    gatewayService.getCustomers(clientId!, opts, query)
      .then((customersResponse: any) => {
        setState(prevState => ({
          ...prevState,
          customers: {
            loading: false,
            total: customersResponse.total,
            data: [
              ...JSON.parse(JSON.stringify(customersResponse.data))
            ],
            fields: customersResponse.fields,
            offset: customersResponse.offset,
            error: null
          }
        }));
      })
      .catch((err) => {
        const error: NetworkError = getNetworkErrors([err])[0];

        setState(prevState => ({
          ...prevState,
          customers: {
            ...prevState.customers,
            loading: false,
            error
          }
        }));

        addToast({
          type: 'error',
          content: error.message
        });
      });
  }, [
    state.customers,
    clientId,
    addToast
  ]);

  const getCustomerHeaderConfig = useCallback((): ITableHeader[] => {
    return [
      {
        key: 'firstName',
        label: 'Name',
        width: '33.33%',
        type: CellType.Text,
        sortable: true,
        editable: true,
        format: (_, row) => `${row.firstName} ${row.lastName}`
      },
      {
        key: 'businessName',
        label: 'Company name',
        width: '33.33%',
        type: CellType.Text,
        sortable: true,
      },
      {
        key: 'phone',
        label: 'Phone',
        width: '33.33%',
        type: CellType.Text,
      },
      {
        key: 'balance',
        label: 'Balance',
        width: '33.33%',
        sortable: true,
        type: CellType.Currency,
        format: (value: number) => formatCurrency(value)
      },
      {
        key: 'bookings',
        label: 'Bookings',
        width: '33.33%',
        sortable: true,
        type: CellType.Number
      }
    ];
  }, []);

  const onRowClick = useCallback((row: any) => {
    navigate({ pathname: `/${clientId}/back-office/customers/${row._id}` });
  }, [
    clientId,
    navigate
  ]);

  const [debounceFetchCustomers] = useDebouncedCallback((query: string) => {
    fetchCustomers({}, query);
  }, 500);

  const renderCustomers = useCallback(() => {
    if (state.customers.loading) {
      return (
        <Spinner
          color={theme.textColor}
          size={'M'}
        />
      );
    }

    // TODO: show error

    if (!state.customers.data) {
      return null;
    }

    return (
      <Table
        namespaceKey={'customers'}
        loading={state.customers.loading}
        headerConfig={getCustomerHeaderConfig()}
        rows={state.customers.data! || []}
        total={state.customers.total!}
        offset={state.customers.offset}
        footerContent={customerPaginationMarkup()}
        sortState={customerSortState}
        onRowClick={onRowClick}
        onSort={customerSetSortState}
      />
    );
  }, [
    state.customers,
    customerSortState,
    customerSetSortState,
    customerPaginationMarkup,
    getCustomerHeaderConfig,
    onRowClick,
  ]);

  useEffect(() => {
    if (!state.customers.data && !state.customers.error) {
      fetchCustomers();
    }
  }, [
    state.customers.data,
    state.customers.error,
    fetchCustomers
  ]);

  return (
    <Wrapper>
      <PageHeader
        title={'Customers'}
      />
      <AdminFormLine>
        <Card>
          <Search onSearch={debounceFetchCustomers} />
          {renderCustomers()}
        </Card>
      </AdminFormLine>
    </Wrapper>
  );
};

export default memo(Customers);

